// ============================================================
// ProPilote - Moteur de tarification centralisé
// ============================================================
// Ce fichier est la SEULE source de vérité pour tous les calculs de prix.
// Doit rester en symbiose avec /backend/pricing_engine.py (miroir Python).
// ============================================================

// ------------------- CONFIG DES PLANS -------------------

export const PLAN_CONFIG = {
  chauffeur: {
    id: 'chauffeur',
    name: 'App Chauffeur',
    basePrice: 0,
    vehiclesIncluded: 0,
    isFree: true,
    isStandalone: true,
  },
  entretien: {
    id: 'entretien',
    name: 'Entretien',
    basePrice: 19.99,
    vehiclesIncluded: 5,
    hasVehiclePricing: true,
  },
  atelier: {
    id: 'atelier',
    name: 'Atelier Indépendant',
    basePrice: 19.99,
    vehiclesIncluded: 20,
    extraPerVehicle: 1.00, // Prix flat pour Atelier (spécifique)
    isStandalone: true,
    hasVehiclePricing: true,
  },
  localisation: {
    id: 'localisation',
    name: 'Localisation',
    basePrice: 39.99,
    vehiclesIncluded: 5,
    hasVehiclePricing: true,
  },
  repartition: {
    id: 'repartition',
    name: 'Répartition',
    basePrice: 69.99,
    vehiclesIncluded: 5,
    hasVehiclePricing: true,
    includes: ['entretien', 'localisation'],
  },
  facturation: {
    id: 'facturation',
    name: 'Facturation',
    basePrice: 99.99,
    vehiclesIncluded: 5,
    hasVehiclePricing: true,
    includes: ['entretien', 'localisation', 'repartition'],
  },
  depenses: {
    id: 'depenses',
    name: 'Gestion des Dépenses',
    basePrice: 14.99,
    vehiclesIncluded: 0,
    isAddon: true,
  },
  logistique_plus: {
    id: 'logistique_plus',
    name: 'Logistique Plus',
    basePrice: 29.00,
    isAddon: true,
    hasCustomPricing: true, // Prix calculé par computeLogistiquePlus()
  },
};

// ------------------- PALIERS VÉHICULES DÉGRESSIFS -------------------
// Prix par véhicule SUPPLÉMENTAIRE (au-delà des 5 inclus)

export const VEHICLE_TIERS = [
  { from: 1,  to: 5,   pricePerVehicle: 3.00 }, // 1er au 5e supp
  { from: 6,  to: 15,  pricePerVehicle: 2.50 }, // 6e au 15e supp
  { from: 16, to: 45,  pricePerVehicle: 2.00 }, // 16e au 45e supp
  { from: 46, to: Infinity, pricePerVehicle: 1.50 }, // 46e+
];

// ------------------- PALIERS RÉPARTITEURS (Logistique Plus) -------------------

export const DISPATCHER_TIERS = [
  { from: 1,  to: 2,   pricePerSeat: 25.00 },
  { from: 3,  to: 5,   pricePerSeat: 20.00 },
  { from: 6,  to: 10,  pricePerSeat: 15.00 },
  { from: 11, to: Infinity, pricePerSeat: 10.00 },
];

// ------------------- BUNDLE MULTI-MODULES -------------------

export const BUNDLE_DISCOUNTS = [
  { modules: 1, discount: 0.00 },
  { modules: 2, discount: 0.05 }, // 5%
  { modules: 3, discount: 0.10 }, // 10%
  { modules: 4, discount: 0.15 }, // 15% (aussi pour 5+)
];

// ------------------- FONCTIONS DE CALCUL -------------------

/**
 * Prix pour X véhicules supplémentaires selon les paliers dégressifs.
 * @param {number} extraVehicles Nombre de véhicules au-delà des inclus.
 * @param {number} [flatPrice] Prix flat optionnel (ex: Atelier à 1$/véh).
 * @returns {number} Coût total mensuel pour les véhicules supplémentaires.
 */
export function computeVehicleCost(extraVehicles, flatPrice = null) {
  if (extraVehicles <= 0) return 0;
  if (flatPrice !== null) return round2(extraVehicles * flatPrice);

  let total = 0;
  let remaining = extraVehicles;
  for (const tier of VEHICLE_TIERS) {
    if (remaining <= 0) break;
    const tierCapacity = tier.to === Infinity ? remaining : (tier.to - tier.from + 1);
    const used = Math.min(remaining, tierCapacity);
    total += used * tier.pricePerVehicle;
    remaining -= used;
  }
  return round2(total);
}

/**
 * Prix Logistique Plus complet.
 * @param {object} params
 * @param {number} params.dispatchers Nombre de répartiteurs.
 * @param {number} params.salesAgents Nombre d'agents ventes (3 inclus).
 * @param {number} params.ordersPerMonth Nombre de commandes/mois (1000 incluses).
 * @returns {object} Breakdown détaillé.
 */
export function computeLogistiquePlus({ dispatchers = 0, salesAgents = 3, ordersPerMonth = 0 } = {}) {
  const base = 29.00;

  // Répartiteurs dégressifs
  let dispatcherCost = 0;
  let remaining = dispatchers;
  for (const tier of DISPATCHER_TIERS) {
    if (remaining <= 0) break;
    const tierCapacity = tier.to === Infinity ? remaining : (tier.to - tier.from + 1);
    const used = Math.min(remaining, tierCapacity);
    dispatcherCost += used * tier.pricePerSeat;
    remaining -= used;
  }

  // Agents ventes (3 inclus)
  const extraAgents = Math.max(0, salesAgents - 3);
  const agentsCost = extraAgents * 5;

  // Commandes (1000 incluses)
  const extraOrders = Math.max(0, ordersPerMonth - 1000);
  const ordersCost = extraOrders * 0.02;

  const total = base + dispatcherCost + agentsCost + ordersCost;

  return {
    base,
    dispatcherCost: round2(dispatcherCost),
    agentsCost: round2(agentsCost),
    ordersCost: round2(ordersCost),
    total: round2(total),
    breakdown: {
      dispatchers,
      extraAgents,
      extraOrders,
    }
  };
}

/**
 * Escompte bundle basé sur le nombre de modules facturables sélectionnés.
 * Ne compte pas les modules gratuits/inclus par un autre plan.
 */
export function computeBundleDiscount(nbBillableModules) {
  if (nbBillableModules >= 4) return 0.15;
  const tier = BUNDLE_DISCOUNTS.find(t => t.modules === nbBillableModules);
  return tier ? tier.discount : 0;
}

/**
 * Calcul complet du devis client.
 * @param {object} params
 * @param {string[]} params.selectedModules IDs des modules choisis.
 * @param {number} params.vehicles Nombre total de véhicules gérés.
 * @param {object} params.logistiquePlusParams Params LP si sélectionné.
 * @param {boolean} params.yearly Facturation annuelle (2 mois offerts = -16.67%).
 * @returns {object} Devis complet.
 */
export function computeQuote({
  selectedModules = [],
  vehicles = 5,
  logistiquePlusParams = null,
  yearly = false,
} = {}) {
  const lines = [];

  // Résoudre les inclusions : facturation inclut répartition, etc.
  const effectivelyIncluded = new Set();
  for (const modId of selectedModules) {
    const cfg = PLAN_CONFIG[modId];
    if (cfg?.includes) cfg.includes.forEach(id => effectivelyIncluded.add(id));
  }

  // Ne facturer que le module le plus haut de sa chaîne + les vrais modules distincts
  const billableModules = selectedModules.filter(id => !effectivelyIncluded.has(id));

  // Compter les modules distincts facturables (pour bundle)
  const nbBillable = billableModules.length;
  const bundleDiscount = computeBundleDiscount(nbBillable);

  let subtotal = 0;

  for (const modId of billableModules) {
    const cfg = PLAN_CONFIG[modId];
    if (!cfg) continue;

    let modCost = cfg.basePrice;
    let vehicleCost = 0;

    // Coût véhicules si applicable
    if (cfg.hasVehiclePricing && vehicles > cfg.vehiclesIncluded) {
      const extraVehicles = vehicles - cfg.vehiclesIncluded;
      vehicleCost = computeVehicleCost(extraVehicles, cfg.extraPerVehicle || null);
    }

    // Logistique Plus custom
    if (cfg.hasCustomPricing && cfg.id === 'logistique_plus') {
      const lp = computeLogistiquePlus(logistiquePlusParams || {});
      modCost = lp.total;
      lines.push({
        module: cfg.name,
        base: cfg.basePrice,
        vehicleCost: 0,
        subtotal: modCost,
        details: lp,
      });
      subtotal += modCost;
      continue;
    }

    const modTotal = modCost + vehicleCost;
    lines.push({
      module: cfg.name,
      base: cfg.basePrice,
      vehicleCost: round2(vehicleCost),
      subtotal: round2(modTotal),
    });
    subtotal += modTotal;
  }

  const bundleSaving = round2(subtotal * bundleDiscount);
  const monthlyAfterBundle = round2(subtotal - bundleSaving);
  const yearlyGross = monthlyAfterBundle * 12;
  const yearlySaving = yearly ? round2(monthlyAfterBundle * 2) : 0; // 2 mois offerts
  const yearlyNet = yearly ? round2(yearlyGross - yearlySaving) : yearlyGross;

  return {
    lines,
    subtotal: round2(subtotal),
    bundleDiscount,
    bundleSaving,
    monthly: monthlyAfterBundle,
    yearly: {
      gross: round2(yearlyGross),
      saving: yearlySaving,
      net: yearlyNet,
    },
    billableModules,
    effectivelyIncluded: Array.from(effectivelyIncluded),
    nbBillable,
  };
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

// ------------------- SAVINGS ESTIMATOR (ROI) -------------------

/**
 * Estimations conservatrices d'économies annuelles par véhicule.
 */
export function estimateAnnualSavings(vehicles) {
  return {
    fuel: vehicles * 480,       // ~40$/mois par camion (optimisation carburant)
    admin: vehicles * 360,      // ~30$/mois temps admin épargné
    compliance: vehicles * 240, // ~20$/mois amendes/retards évités
    total: vehicles * 1080,
  };
}
